const STORAGE_KEY_SUFFIX = 'columnPrefs';

export const getColumnPreferencesStorageKey = (gridId, userInfo) => {
  if (!userInfo || !userInfo.current_tenant || !userInfo.username) {
    return `${gridId}-${STORAGE_KEY_SUFFIX}`;
  }
  return `${userInfo.current_tenant}-${userInfo.username}-${gridId}-${STORAGE_KEY_SUFFIX}`;
};

export const saveColumnPreferences = (gridId, userInfo, preferences) => {
  try {
    const storageKey = getColumnPreferencesStorageKey(gridId, userInfo);
    localStorage.setItem(storageKey, JSON.stringify(preferences));
    return true;
  } catch (error) {
    console.error('Error saving column preferences:', error);
    return false;
  }
};

export const loadColumnPreferences = (gridId, userInfo) => {
  try {
    const storageKey = getColumnPreferencesStorageKey(gridId, userInfo);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    console.error('Error loading column preferences:', error);
    return null;
  }
};

export const updateColumnVisibility = (gridId, userInfo, columnName, visible) => {
  const currentPrefs = loadColumnPreferences(gridId, userInfo) || { visibilityMap: {} };

  const updatedPrefs = {
    ...currentPrefs,
    visibilityMap: {
      ...currentPrefs.visibilityMap,
      [columnName]: visible
    }
  };

  saveColumnPreferences(gridId, userInfo, updatedPrefs);
  return updatedPrefs;
};

export const updateColumnOrder = (gridId, userInfo, columnOrder) => {
  const currentPrefs = loadColumnPreferences(gridId, userInfo) || { visibilityMap: {} };

  const updatedPrefs = {
    ...currentPrefs,
    columnOrder
  };

  saveColumnPreferences(gridId, userInfo, updatedPrefs);
  return updatedPrefs;
};

export const updateColumnWidth = (gridId, userInfo, columnName, width) => {
  const numericWidth = Number(width);
  if (!columnName || !Number.isFinite(numericWidth) || numericWidth <= 0) {
    return loadColumnPreferences(gridId, userInfo) || { visibilityMap: {} };
  }

  const currentPrefs = loadColumnPreferences(gridId, userInfo) || { visibilityMap: {} };

  const updatedPrefs = {
    ...currentPrefs,
    widthMap: {
      ...currentPrefs.widthMap,
      [columnName]: numericWidth
    }
  };

  saveColumnPreferences(gridId, userInfo, updatedPrefs);
  return updatedPrefs;
};

export const applyColumnPreferences = (columns, preferences) => {
  if (!preferences) {
    return columns;
  }

  // First apply visibility and width preferences
  let updatedColumns = columns.map(column => {
    const columnName = column.name;
    let updatedColumn = column;

    if (columnName && preferences.visibilityMap && preferences.visibilityMap.hasOwnProperty(columnName)) {
      updatedColumn = {
        ...updatedColumn,
        visible: preferences.visibilityMap[columnName],
        defaultVisible: preferences.visibilityMap[columnName]
      };
    }

    const savedWidth = columnName && preferences.widthMap ? Number(preferences.widthMap[columnName]) : null;
    if (Number.isFinite(savedWidth) && savedWidth > 0) {
      updatedColumn = {
        ...updatedColumn,
        width: savedWidth,
        defaultWidth: savedWidth,
        flex: null,
        defaultFlex: null
      };
    }

    return updatedColumn;
  });

  // Then apply column order if saved
  if (preferences.columnOrder && Array.isArray(preferences.columnOrder)) {
    const orderedColumns = [];
    const columnMap = new Map(updatedColumns.map(col => [col.name, col]));

    // Add columns in saved order
    preferences.columnOrder.forEach(colName => {
      if (columnMap.has(colName)) {
        orderedColumns.push(columnMap.get(colName));
        columnMap.delete(colName);
      }
    });

    // Add any remaining columns that weren't in the saved order (new columns)
    columnMap.forEach(col => {
      orderedColumns.push(col);
    });

    updatedColumns = orderedColumns;
  }

  return updatedColumns;
};

export const clearColumnPreferences = (gridId, userInfo) => {
  try {
    const storageKey = getColumnPreferencesStorageKey(gridId, userInfo);
    localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    console.error('Error clearing column preferences:', error);
    return false;
  }
};
